import { InsuranceType, useBrokerProductLink } from '@jove-frontend/uikit';

type QuoteExitRedirectParams = {
  promotionInfo?: any;
  insuranceType?: string;
  // Caller specific default url, used only when no custom exit url is configured on the product
  fallbackUrl?: string;
  redirectMode?: 'href' | 'replace';
};

export const useQuoteCloseWindow = () => {
  const { getBrokerProductLandingPageUrl } = useBrokerProductLink?.() || {};
  const getJumpToLandingHomePageUrl = (data: { promotionInfo?: any; insuranceType?: string }) => {
    let url = '';
    if (data?.promotionInfo) {
      const { insuranceType, payload, slug, insuranceProviders, regions, countries } = data.promotionInfo;
      url = getBrokerProductLandingPageUrl?.({
        insuranceType,
        slug,
        providerCode: insuranceProviders?.[0]?.code,
        landingPageThemes: payload?.landing_page_theme,
        regions,
        countries,
      });
    } else if (data?.insuranceType) {
      const isGit = data.insuranceType === InsuranceType.GOODS_IN_TRANSIT_INSURANCE;
      url = `${process.env.NX_WEBSITE_URL}${isGit ? '/personal/goods-in-transit' : ''}`;
    } else {
      url = process.env.NX_WEBSITE_URL;
    }
    return url;
  };

  const isQuoteEmbeddedInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (err) {
      // Access to window.top is restricted on cross-origin embeds, treat it as embedded
      return true;
    }
  };

  // Single entry for resolving the quote exit target url. The custom exit redirect url
  // configured on the broker product takes the highest priority, then the caller specific
  // fallback, then the legacy landing page logic. Add future exit rules here so the
  // exit call sites do not need to change.
  const getQuoteExitRedirectUrl = (params?: QuoteExitRedirectParams) => {
    const { promotionInfo, insuranceType, fallbackUrl } = params || {};
    const customExitRedirectUrl = promotionInfo?.payload?.quote_process_settings?.quote_exit_redirect_url;

    return customExitRedirectUrl || fallbackUrl || getJumpToLandingHomePageUrl({ promotionInfo, insuranceType }) || process.env.NX_WEBSITE_URL;
  };

  const closeQuoteAndPostParentMessage = () => {
    setTimeout(() => {
      parent.postMessage('close-iframe', '*');
    }, 200);
  };

  const exitQuoteAndRedirect = (params?: QuoteExitRedirectParams) => {
    const url = getQuoteExitRedirectUrl(params);

    // Embedded in a third-party iframe: the top window belongs to the parent page, so notify
    // the parent to close the embed and keep the self redirect as a fallback in case the
    // parent does not handle the message. Standalone window: navigate directly.
    if (isQuoteEmbeddedInIframe()) {
      closeQuoteAndPostParentMessage();
    }

    if (params?.redirectMode === 'replace') {
      window.location.replace(url);
    } else {
      window.location.href = url;
    }
  };

  return {
    getJumpToLandingHomePageUrl,
    getQuoteExitRedirectUrl,
    closeQuoteAndPostParentMessage,
    exitQuoteAndRedirect,
  };
};
